import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import useViewTransition from "@/hooks/useViewTransition";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Ingredients from "@/pages/Ingredients";
import Recipes from "@/pages/Recipes";
import RecipeDetail from "@/pages/RecipeDetail";
import Staff from "@/pages/Staff";
import Roster from "@/pages/Roster";
import Inventory from "@/pages/Inventory";
import Ledger from "@/pages/Ledger";
import Snapshots from "@/pages/Snapshots";
import PublicInventoryShare from "@/pages/PublicInventoryShare";
import { BlogList, BlogEditor } from "@/pages/BlogAdmin";
import Contributors from "@/pages/Contributors";
import MediaMentions from "@/pages/MediaMentions";
import AdminDashboard from "@/pages/AdminDashboard";
import AuthorArchive from "@/pages/AuthorArchive";
import { PublicBlogList, PublicBlogPost } from "@/pages/PublicBlog";
import Press from "@/pages/Press";
import AuthCallback from "@/pages/AuthCallback";
import PublicRecipe from "@/pages/PublicRecipe";
import Billing from "@/pages/Billing";
import Landing from "@/pages/Landing";
import Trust from "@/pages/Trust";
import Pricing from "@/pages/Pricing";
import PosConnect from "@/pages/PosConnect";
import SyncEngine from "@/pages/SyncEngine";
import ResourceHub from "@/pages/resources/ResourceHub";
import FoodCost from "@/pages/resources/FoodCost";
import LabourCost from "@/pages/resources/LabourCost";
import RecipeFormulas from "@/pages/resources/RecipeFormulas";
import BulkConversion from "@/pages/resources/BulkConversion";
import { Toaster } from "sonner";

function AppRouter() {
  const location = useLocation();
  useViewTransition();
  // Emergent Google OAuth returns as /path#session_id=<id>
  // Handle it BEFORE ProtectedRoute so we can exchange it for a real session.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public marketing routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/blog" element={<PublicBlogList />} />
      <Route path="/blog/:slug" element={<PublicBlogPost />} />
      <Route path="/press" element={<Press />} />
      <Route path="/trust" element={<Trust />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/r/:id" element={<PublicRecipe />} />
      <Route path="/authors/:slug" element={<AuthorArchive />} />
      <Route path="/inventory/share/:token" element={<PublicInventoryShare />} />
      <Route path="/resources" element={<ResourceHub />} />
      <Route path="/resources/food-cost" element={<FoodCost />} />
      <Route path="/resources/labour-cost" element={<LabourCost />} />
      <Route path="/resources/recipe-formulas" element={<RecipeFormulas />} />
      <Route path="/resources/bulk-conversion" element={<BulkConversion />} />

      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/ingredients" element={<Ingredients />} />
        <Route path="/recipes" element={<Recipes />} />
        <Route path="/recipes/:id" element={<RecipeDetail />} />
        <Route path="/staff" element={<Staff />} />
        <Route path="/roster" element={<Roster />} />
        <Route path="/inventory" element={<Inventory />} />
        <Route path="/pos" element={<PosConnect />} />
        <Route path="/sync" element={<SyncEngine />} />
        <Route path="/ledger" element={<Ledger />} />
        <Route path="/snapshots" element={<Snapshots />} />
        <Route path="/admin/blog" element={<BlogList />} />
        <Route path="/admin/blog/new" element={<BlogEditor />} />
        <Route path="/admin/blog/:id" element={<BlogEditor />} />
        <Route path="/admin/contributors" element={<Contributors />} />
        <Route path="/admin/media-mentions" element={<MediaMentions />} />
        <Route path="/admin/system" element={<AdminDashboard />} />
        <Route path="/billing" element={<Billing />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton theme="dark" />
    </AuthProvider>
  );
}

export default App;
